package com.schoolconnect.app.repository.subject

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.SubjectCreateRequest
import com.schoolconnect.app.datasource.network.request.SubjectUpdateRequest
import com.schoolconnect.app.datasource.network.request.SubjectFilterRequest
import com.schoolconnect.app.datasource.network.response.SubjectAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse
import com.schoolconnect.app.datasource.network.response.SubjectResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SubjectRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SubjectRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class SubjectsPageCacheKey(
        val schoolId: String,
        val query: String,
        val category: String?,
        val isActive: Boolean?,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedSubjectsPage(
        val key: SubjectsPageCacheKey,
        val subjects: List<SubjectResponse>
    )

    private data class CachedSubjectAnalytics(
        val schoolId: String,
        val analytics: CachedValue<SubjectAnalyticsResponse>
    )

    private data class CachedSubjectById(
        val id: String,
        val subject: SubjectResponse
    )

    private data class CachedSubjectCodeMap(
        val schoolId: String,
        val codes: CachedValue<Map<String, String>>
    )

    data class SchoolSubjectOptions(
        val codeById: Map<String, String>,
        val nameByCode: Map<String, String>,
        val nameById: Map<String, String>
    )

    private data class CachedSchoolSubjectOptions(
        val schoolId: String,
        val options: CachedValue<SchoolSubjectOptions>
    )

    private var cachedSubjectsPage: CachedSubjectsPage? = null
    private var cachedSubjectAnalytics: CachedSubjectAnalytics? = null
    private var cachedSubjectById: CachedSubjectById? = null
    private var cachedSubjectCodeMap: CachedSubjectCodeMap? = null
    private var cachedSchoolSubjectOptions: CachedSchoolSubjectOptions? = null

    internal fun buildSchoolSubjectOptions(rows: List<SubjectResponse>): SchoolSubjectOptions {
        val subjects = rows.distinctBy { it.id }
        return SchoolSubjectOptions(
            codeById = subjects.mapNotNull { row ->
                val id = row.id.orEmpty().trim()
                val code = row.shortCode.orEmpty().trim()
                if (id.isBlank() || code.isBlank()) null else id to code
            }.toMap(),
            nameByCode = subjects.mapNotNull { row ->
                val code = row.shortCode.orEmpty().trim().uppercase()
                val name = row.name.orEmpty().trim()
                if (code.isBlank() || name.isBlank()) null else code to name
            }.toMap(),
            nameById = subjects.mapNotNull { row ->
                val id = row.id.orEmpty().trim()
                val name = row.name.orEmpty().trim()
                if (id.isBlank() || name.isBlank()) null else id to name
            }.toMap()
        )
    }

    suspend fun loadSchoolSubjectsPage(
        schoolId: String,
        query: String = "",
        category: String? = null,
        isActive: Boolean? = null,
        advancedFilter: SubjectFilterRequest? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("short_code", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<SubjectResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val normalizedQuery = query.trim()
        val normalizedCategory = category?.trim()?.takeIf { it.isNotBlank() }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = SubjectsPageCacheKey(
            schoolId = normalizedSchoolId,
            query = normalizedQuery,
            category = normalizedCategory,
            isActive = isActive,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedSubjectsPage?.takeIf { !forceRemote && advancedFilter == null && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolSubjectsPage cacheHit school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} category=${normalizedCategory ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.subjects.size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolSubjectsPage", "school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize", cache.subjects) {
                loadSchoolSubjectsPage(normalizedSchoolId, query, normalizedCategory, isActive, null, safePageIndex, safePageSize, sort, true)
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolSubjectsPage start school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} category=${normalizedCategory ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val subjects = if (advancedFilter != null) {
                KtorApiClient.filterSubjects(
                    request = advancedFilter,
                    limit = safePageSize,
                    offset = offset,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue
                ).getOrElse { throwable ->
                    scLog("$TAG loadSchoolSubjectsPage advancedFailure school=$normalizedSchoolId", throwable)
                    throw throwable
                }
            } else KtorApiClient.getSubjects(
                schoolId = normalizedSchoolId,
                category = normalizedCategory,
                query = normalizedQuery.ifBlank { null },
                isActive = isActive,
                limit = safePageSize,
                offset = offset,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).getOrElse { throwable ->
                scLog(
                    "$TAG loadSchoolSubjectsPage failure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                    throwable
                )
                throw throwable
            }
            val deduped = subjects.distinctBy { it.id }
                .withServerTotalCount(subjects.serverTotalCountOrNull())
            if (advancedFilter == null) {
                cachedSubjectsPage = CachedSubjectsPage(cacheKey, deduped)
            }
            scLog(
                "$TAG loadSchoolSubjectsPage complete school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${deduped.size}"
            )
            deduped
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolSubjectsPage failure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    suspend fun loadSchoolSubjectsAnalytics(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<SubjectAnalyticsResponse> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        cachedSubjectAnalytics?.takeIf { !forceRemote && it.schoolId == normalizedSchoolId }?.let { cache ->
            scLog(
                "$TAG loadSchoolSubjectsAnalytics cacheHit school=$normalizedSchoolId total=${cache.analytics.value.totalSubjects} active=${cache.analytics.value.activeSubjects} inactive=${cache.analytics.value.inactiveSubjects}"
            )
            return Result.success(cache.analytics.value)
        }

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolSubjectsAnalytics",
            context = "school=$normalizedSchoolId forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedSubjectAnalytics?.takeIf { it.schoolId == normalizedSchoolId }?.analytics,
            fetchValue = {
                KtorApiClient.getSchoolStatistics(normalizedSchoolId).mapCatching { response ->
                    response.toSubjectAnalytics()
                }
            },
            updateCache = { analytics, fetchedAt ->
                cachedSubjectAnalytics = CachedSubjectAnalytics(
                    schoolId = normalizedSchoolId,
                    analytics = CachedValue(analytics, fetchedAt)
                )
            },
            successDetails = { analytics ->
                "total=${analytics.totalSubjects} active=${analytics.activeSubjects} inactive=${analytics.inactiveSubjects}"
            }
        )
    }

    fun peekSchoolSubjectsAnalytics(schoolId: String): SubjectAnalyticsResponse? =
        cachedSubjectAnalytics?.takeIf { it.schoolId == schoolId.trim() }?.analytics?.value

    suspend fun loadSubjectById(
        subjectId: String,
        forceRemote: Boolean = false
    ): Result<SubjectResponse> {
        val normalizedId = subjectId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("subjectId is required"))
        }

        cachedSubjectById?.takeIf { !forceRemote && it.id == normalizedId }?.let { cache ->
            scLog("$TAG loadSubjectById cacheHit subjectId=$normalizedId")
            return Result.success(cache.subject)
        }

        scLog("$TAG loadSubjectById start subjectId=$normalizedId forceRemote=$forceRemote")
        return runCatching {
            val subject = KtorApiClient.getSubject(normalizedId).getOrElse { throwable ->
                scLog("$TAG loadSubjectById failure subjectId=$normalizedId", throwable)
                throw throwable
            }
            cachedSubjectById = CachedSubjectById(normalizedId, subject)
            scLog("$TAG loadSubjectById complete subjectId=$normalizedId schoolId=${subject.schoolId}")
            subject
        }
    }

    suspend fun loadSubjectClassAssignments(
        schoolId: String,
        subjectCode: String,
        forceRemote: Boolean = false
    ): Result<List<Pair<String, String>>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedSubjectCode = subjectCode.trim()
        if (normalizedSchoolId.isBlank() || normalizedSubjectCode.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and subjectCode are required"))
        }

        scLog(
            "$TAG loadSubjectClassAssignments start school=$normalizedSchoolId subjectCode=$normalizedSubjectCode forceRemote=$forceRemote"
        )
        return ClassSectionRepository.loadSchoolClassSectionLookup(
            schoolId = normalizedSchoolId,
            forceRemote = forceRemote
        ).map { rows ->
            rows.filter { row ->
                row.subjectCodes.orEmpty().any { it.trim().equals(normalizedSubjectCode, ignoreCase = true) }
            }.map { row ->
                row.className.orEmpty().trim() to row.sectionName.orEmpty().trim()
            }.filter { (className, sectionName) -> className.isNotBlank() || sectionName.isNotBlank() }
                .distinct()
        }.onSuccess { assignments ->
            scLog(
                "$TAG loadSubjectClassAssignments complete school=$normalizedSchoolId subjectCode=$normalizedSubjectCode count=${assignments.size}"
            )
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSubjectClassAssignments failure school=$normalizedSchoolId subjectCode=$normalizedSubjectCode",
                throwable
            )
        }
    }

    suspend fun loadSchoolSubjectShortCodeMap(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<Map<String, String>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        cachedSubjectCodeMap?.takeIf { !forceRemote && it.schoolId == normalizedSchoolId }?.let { cache ->
            scLog(
                "$TAG loadSchoolSubjectShortCodeMap cacheHit school=$normalizedSchoolId count=${cache.codes.value.size}"
            )
            return Result.success(cache.codes.value)
        }

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolSubjectShortCodeMap",
            context = "school=$normalizedSchoolId forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedSubjectCodeMap?.takeIf { it.schoolId == normalizedSchoolId }?.codes,
            fetchValue = {
                runCatching {
                    val rows = loadPagedItems(
                        tag = TAG,
                        operation = "loadSchoolSubjectShortCodeMapRows",
                        pageSize = PAGE_SIZE,
                        maxPages = MAX_PAGES,
                        startContext = "school=$normalizedSchoolId"
                    ) { offset ->
                        KtorApiClient.getSubjects(
                            schoolId = normalizedSchoolId,
                            category = null,
                            query = null,
                            isActive = null,
                            limit = PAGE_SIZE,
                            offset = offset
                        ).getOrElse { throwable ->
                            scLog(
                                "$TAG loadSchoolSubjectShortCodeMap pageFailure school=$normalizedSchoolId offset=$offset",
                                throwable
                            )
                            throw throwable
                        }
                    }.distinctBy { it.id }

                    rows.mapNotNull { row ->
                        val id = row.id.orEmpty()
                        val shortCode = row.shortCode.orEmpty()
                        if (id.isBlank() || shortCode.isBlank()) null else id to shortCode
                    }.toMap()
                }.fold(
                    onSuccess = { Result.success(it) },
                    onFailure = { Result.failure(it) }
                )
            },
            updateCache = { codes, fetchedAt ->
                cachedSubjectCodeMap = CachedSubjectCodeMap(
                    schoolId = normalizedSchoolId,
                    codes = CachedValue(codes, fetchedAt)
                )
            },
            successDetails = { codes -> "count=${codes.size}" }
        )
    }

    suspend fun loadSchoolSubjectNameMap(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<Map<String, String>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolSubjectNameMap",
            context = "school=$normalizedSchoolId forceRemote=$forceRemote",
            cachedValue = null,
            fetchValue = {
                runCatching {
                    val rows = loadPagedItems(
                        tag = TAG,
                        operation = "loadSchoolSubjectNameMapRows",
                        pageSize = PAGE_SIZE,
                        maxPages = MAX_PAGES,
                        startContext = "school=$normalizedSchoolId"
                    ) { offset ->
                        KtorApiClient.getSubjects(
                            schoolId = normalizedSchoolId,
                            category = null,
                            query = null,
                            isActive = null,
                            limit = PAGE_SIZE,
                            offset = offset
                        ).getOrElse { throwable ->
                            scLog(
                                "$TAG loadSchoolSubjectNameMap pageFailure school=$normalizedSchoolId offset=$offset",
                                throwable
                            )
                            throw throwable
                        }
                    }.distinctBy { it.id }

                    rows.mapNotNull { row ->
                        val code = row.shortCode.orEmpty().uppercase()
                        val name = row.name.orEmpty()
                        if (code.isBlank() || name.isBlank()) null else code to name
                    }.toMap()
                }.fold(
                    onSuccess = { Result.success(it) },
                    onFailure = { Result.failure(it) }
                )
            },
            updateCache = { _, _ -> },
            successDetails = { names -> "count=${names.size}" }
        )
    }

    suspend fun loadSchoolSubjectOptions(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<SchoolSubjectOptions> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val cached = cachedSchoolSubjectOptions?.takeIf {
            it.schoolId == normalizedSchoolId
        }?.options

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolSubjectOptions",
            context = "school=$normalizedSchoolId forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cached,
            fetchValue = {
                runCatching {
                    val rows = loadPagedItems(
                        tag = TAG,
                        operation = "loadSchoolSubjectOptionsRows",
                        pageSize = PAGE_SIZE,
                        maxPages = MAX_PAGES,
                        startContext = "school=$normalizedSchoolId"
                    ) { offset ->
                        KtorApiClient.getSubjects(
                            schoolId = normalizedSchoolId,
                            category = null,
                            query = null,
                            isActive = null,
                            limit = PAGE_SIZE,
                            offset = offset
                        ).getOrElse { throw it }
                    }.distinctBy { it.id }

                    buildSchoolSubjectOptions(rows)
                }.fold(
                    onSuccess = { Result.success(it) },
                    onFailure = { Result.failure(it) }
                )
            },
            updateCache = { options, fetchedAt ->
                cachedSchoolSubjectOptions = CachedSchoolSubjectOptions(
                    schoolId = normalizedSchoolId,
                    options = CachedValue(options, fetchedAt)
                )
            },
            successDetails = { options ->
                "codes=${options.codeById.size} names=${options.nameByCode.size}"
            }
        )
    }

    suspend fun createSubject(
        schoolId: String,
        request: SubjectCreateRequest
    ): Result<SubjectResponse> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        scLog(
            "$TAG createSubject start school=$normalizedSchoolId shortCode=${request.shortCode.ifBlank { "<blank>" }} name=${request.name.ifBlank { "<blank>" }}"
        )
        return KtorApiClient.createSubject(request).onSuccess { response ->
            invalidateCaches()
            scLog(
                    "$TAG createSubject success school=${response.schoolId.orEmpty().ifBlank { normalizedSchoolId }} subjectId=${response.id.orEmpty()} shortCode=${response.shortCode.orEmpty()}"
            )
        }.onFailure { throwable ->
            scLog("$TAG createSubject failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun updateSubject(
        subjectId: String,
        request: SubjectUpdateRequest
    ): Result<SubjectResponse> {
        val normalizedSubjectId = subjectId.trim()
        if (normalizedSubjectId.isBlank()) {
            return Result.failure(IllegalArgumentException("subjectId is required"))
        }

        scLog(
            "$TAG updateSubject start subjectId=$normalizedSubjectId shortCode=${request.shortCode ?: "<unchanged>"} name=${request.name ?: "<unchanged>"} isActive=${request.isActive?.toString() ?: "<unchanged>"}"
        )
        return KtorApiClient.updateSubject(
            subjectId = normalizedSubjectId,
            request = request
        ).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG updateSubject success subjectId=$normalizedSubjectId shortCode=${response.shortCode} isActive=${response.isActive}"
            )
        }.onFailure { throwable ->
            scLog("$TAG updateSubject failure subjectId=$normalizedSubjectId", throwable)
        }
    }

    suspend fun toggleSubjectStatus(
        subjectId: String,
        isActive: Boolean
    ): Result<SubjectResponse> {
        val normalizedSubjectId = subjectId.trim()
        if (normalizedSubjectId.isBlank()) {
            return Result.failure(IllegalArgumentException("subjectId is required"))
        }

        scLog("$TAG toggleSubjectStatus start subjectId=$normalizedSubjectId isActive=$isActive")
        return KtorApiClient.updateSubject(
            subjectId = normalizedSubjectId,
            request = SubjectUpdateRequest(isActive = isActive)
        ).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG toggleSubjectStatus success subjectId=$normalizedSubjectId shortCode=${response.shortCode} isActive=${response.isActive}"
            )
        }.onFailure { throwable ->
            scLog("$TAG toggleSubjectStatus failure subjectId=$normalizedSubjectId", throwable)
        }
    }

    suspend fun deleteSubject(subjectId: String): Result<Unit> {
        val normalizedSubjectId = subjectId.trim()
        if (normalizedSubjectId.isBlank()) {
            return Result.failure(IllegalArgumentException("subjectId is required"))
        }

        scLog("$TAG deleteSubject start subjectId=$normalizedSubjectId")
        return KtorApiClient.deleteSubject(normalizedSubjectId).onSuccess {
            invalidateCaches()
            scLog("$TAG deleteSubject success subjectId=$normalizedSubjectId")
        }.onFailure { throwable ->
            scLog("$TAG deleteSubject failure subjectId=$normalizedSubjectId", throwable)
        }
    }

    fun invalidateCaches() {
        cachedSubjectsPage = null
        cachedSubjectAnalytics = null
        cachedSubjectById = null
        cachedSubjectCodeMap = null
        cachedSchoolSubjectOptions = null
    }

    private fun SchoolStatisticsResponse.toSubjectAnalytics(): SubjectAnalyticsResponse = SubjectAnalyticsResponse(
        totalSubjects = statistics.subjects.total,
        activeSubjects = statistics.subjects.active,
        inactiveSubjects = statistics.subjects.inactive
    )
}
